"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
// eslint-disable-next-line import/no-cycle
const types_1 = require("~/store/types");
// eslint-disable-next-line import/no-cycle
const _1 = require(".");
const consts_1 = require("../consts");
// eslint-disable-next-line import/no-cycle
const Viewer_1 = __importDefault(require("../Viewer"));
const utils_1 = require("../utils");
class ExtrusionObjectsBase extends _1.BaseObject3D {
    checkAndFixSize() {
        return this.checkAndFixSizeByAxis(this.scale, 2, consts_1.consts.DEFAULT_MIN_LENGTH, this.partDetails.maxStockLength);
    }
    getOutboundSnappingPointsRaw() {
        return this.getInboundSnappingPointsRaw();
    }
    getInboundSnappingPointsRaw() {
        const points = super.getInboundSnappingPointsRaw();
        return utils_1.Utils.areEqual(this.scalingAngle, 0)
            ? points
            : points && utils_1.Utils.alignRawSnappingPointsForAngledExtrusion(this, points);
    }
    getInboundSnappingPoints() {
        const points = super.getInboundSnappingPoints();
        return utils_1.Utils.areEqual(this.scalingAngle, 0)
            ? points
            : points && utils_1.Utils.alignSnappingPointsForAngledExtrusion(this, points);
    }
    getTransformChildObjects(forDeleting = false) {
        function getMaxUuid(arr) {
            return arr.reduce((max, str) => (str.localeCompare(max) > 0 ? str : max), '');
        }
        const result = [];
        const viewer = Viewer_1.default.instance;
        const { selectedObjects } = viewer;
        this.connectedItems.forEach((connectedItem) => {
            const item = viewer.getObjectById(connectedItem.uuid);
            if (item && item instanceof _1.BaseObject3D) {
                if (item.type === types_1.ITEM_TYPE.COMPONENT) {
                    const connectedExtrusionIds = item.connectedItems
                        .filter((extrusion) => extrusion.itemType === types_1.ITEM_TYPE.EXTRUSION)
                        .map((extrusion) => extrusion.uuid);
                    if (forDeleting && connectedExtrusionIds.includes(this.uuid)) {
                        connectedExtrusionIds.push(this.uuid);
                    }
                    if (connectedExtrusionIds.length <= 1) {
                        result.push(item);
                    }
                    else if (connectedExtrusionIds.every((uuid) => {
                        return selectedObjects.find((selectedObject) => selectedObject.uuid === uuid);
                    })) {
                        const maxUuid = getMaxUuid(connectedExtrusionIds);
                        if (this.uuid === maxUuid) {
                            result.push(item);
                        }
                    }
                }
            }
        });
        return result;
    }
    translate(offset, worldSpace = true) {
        if (!this.userData.noMoveJoinedComps && !utils_1.Utils.areVectorsEqual(offset, utils_1.Utils.zeroVector)) {
            const connectedComponents = this.getTransformChildObjects();
            const { selectedObjects } = Viewer_1.default.instance;
            connectedComponents.forEach((component) => {
                if (!selectedObjects.some((selectedObject) => selectedObject.uuid === component.uuid)) {
                    component.translate(offset);
                }
            });
        }
        super.translate(offset, worldSpace);
        return this;
    }
    rotate(quaternion, worldSpace = true, rotationPoint = null) {
        super.rotate(quaternion, worldSpace, rotationPoint);
        if (this.userData.noMoveJoinedComps) {
            return this;
        }
        this.oldCoord = this.position.clone();
        if (utils_1.Utils.areNotEqual(quaternion.lengthSq(), 1.0)) {
            quaternion.normalize();
        }
        if (utils_1.Utils.areQuaternionsEqual(quaternion, utils_1.Utils.identityQuaternion)) {
            return this;
        }
        const connectedComponents = this.getTransformChildObjects();
        connectedComponents.forEach((comp) => {
            comp.rotate(quaternion, worldSpace, rotationPoint);
        });
        return this;
    }
    getCollisionExcludeIds() {
        const result = super.getCollisionExcludeIds();
        const connectedComponents = this.getTransformChildObjects();
        connectedComponents.forEach((component) => result.push(component.uuid));
        return result;
    }
    getConnectionExcludeIDs() {
        const result = super.getConnectionExcludeIDs();
        this.connectedItems.forEach((connectedItem) => {
            if (connectedItem.itemType === types_1.ITEM_TYPE.EXTRUSION) {
                const object = Viewer_1.default.instance.getObjectById(connectedItem.uuid);
                if (object && object instanceof _1.BaseObject3D) {
                    const connectionObjects = Viewer_1.default.instance
                        .getDesignProMain()
                        .connectionManager.getRelatedConnectionObjects(object);
                    connectionObjects.forEach((connectionObject) => {
                        connectionObject.getFasteners().forEach((fastener) => {
                            if (fastener.partDetails.componentType === types_1.COMPONENT_TYPE.PANEL_GASKET) {
                                result.push(fastener.uuid);
                            }
                        });
                    });
                }
            }
        });
        this.getTransformChildObjects().forEach((connectedComponent) => {
            connectedComponent.connectedItems.forEach((item) => {
                if (item.componentType === types_1.COMPONENT_TYPE.FOOT || item.componentType === types_1.COMPONENT_TYPE.STEM_CASTER) {
                    result.push(item.uuid);
                }
            });
        });
        return result;
    }
    getEndComponents() {
        const endComponents = [];
        const connectedComponents = this.getTransformChildObjects();
        connectedComponents.forEach((component) => {
            const connectedItem = component.connectedItems.find((item) => item.uuid === this.uuid);
            if (connectedItem && connectedItem.connectedSide === 'end' && !utils_1.Utils.isCoverComponent(component)) {
                endComponents.push(component);
                // eslint-disable-next-line no-param-reassign
                component.userData.endFace = connectedItem.endFace;
            }
        });
        return endComponents;
    }
    getCenterPoints() {
        const centerPoint = utils_1.Utils.getAABBoxCenter(this);
        let aPoint = centerPoint.clone().add(this.directionNormal
            .clone()
            .negate()
            .multiplyScalar(this.getLength() / 2));
        let bPoint = centerPoint.clone().add(this.directionNormal.clone().multiplyScalar(this.getLength() / 2));
        if (this.userData.isReverseScale) {
            const tempVector = aPoint.clone();
            aPoint = bPoint.clone();
            bPoint = tempVector;
        }
        return { a: aPoint, b: bPoint };
    }
    placeEndComponents(components, prevPoints) {
        const currPoints = this.getCenterPoints();
        const aComponent = components.filter((comp) => {
            return comp.userData.endFace === 'A';
        })[0];
        let moveVector;
        if (aComponent) {
            moveVector = currPoints.a.clone().sub(prevPoints.a);
            aComponent.translate(moveVector);
        }
        const bComponent = components.filter((comp) => {
            return comp.userData.endFace === 'B';
        })[0];
        if (bComponent) {
            moveVector = currPoints.b.clone().sub(prevPoints.b);
            bComponent.translate(moveVector);
        }
    }
    translateComponentsBeforeScaling(offset) {
        const endComponents = this.getEndComponents();
        endComponents.forEach((comp) => {
            comp.translate(offset);
        });
        const covers = this.getTransformChildObjects().filter((obj) => utils_1.Utils.isCoverComponent(obj));
        covers.forEach((cover) => {
            const coverSurfacePoint = cover.userData.surfacePoint;
            const isOneCoverOnSlot = covers.filter((c) => {
                return c.userData.surfacePoint === coverSurfacePoint;
            }).length === 1;
            const coverLength = cover.getLength();
            const extrusionLength = this.getLength();
            const isCoveredEntireSlot = utils_1.Utils.areEqual(coverLength, extrusionLength);
            if (isOneCoverOnSlot && isCoveredEntireSlot) {
                cover.translate(offset);
            }
        });
    }
    addScale(growth, validateSize = true) {
        if (utils_1.Utils.areVectorsEqual(growth, utils_1.Utils.zeroVector)) {
            return this;
        }
        this.userData.noMoveJoinedComps = true;
        const endComponents = this.getEndComponents();
        const viewer = Viewer_1.default.instance;
        const hasEndComponents = endComponents.length > 0;
        if (hasEndComponents) {
            endComponents.forEach((comp) => {
                // eslint-disable-next-line no-param-reassign
                comp.visible = false;
                viewer.collisionProcessor.notifyObjectRemoved(comp);
            });
        }
        const prevPoints = this.getCenterPoints();
        const prevScale = this.getScale().clone();
        super.addScale(growth, validateSize);
        const newScale = this.getScale();
        if (hasEndComponents) {
            this.placeEndComponents(endComponents, prevPoints);
            endComponents.forEach((comp) => {
                // eslint-disable-next-line no-param-reassign
                comp.visible = true;
                viewer.collisionProcessor.notifyObjectAdded(comp);
            });
        }
        this.scaleCoverComponents(prevScale, newScale);
        delete this.userData.noMoveJoinedComps;
        return this;
    }
    get isSnappable() {
        return utils_1.Utils.areEqual(0, this.scalingAngle);
    }
    get scalingAxes() {
        return [utils_1.Utils.vecToAxis(this.zDirection)];
    }
    scaleCoverComponents(oldScale, newScale) {
        const covers = this.getTransformChildObjects().filter((obj) => utils_1.Utils.isCoverComponent(obj));
        covers.forEach((cover) => {
            const coverSurfacePoint = cover.userData.surfacePoint;
            const isOneCoverOnSlot = covers.filter((c) => {
                return c.userData.surfacePoint === coverSurfacePoint;
            }).length === 1;
            const coverLength = cover.getLength();
            const extrusionLength = newScale.z;
            const isCoveredEntireSlot = utils_1.Utils.areEqual(coverLength, oldScale.z) || coverLength >= newScale.z;
            const addValue = newScale.z - oldScale.z;
            if (isOneCoverOnSlot &&
                isCoveredEntireSlot &&
                ((addValue > 0 && coverLength + addValue <= cover.partDetails.maxStockLength) ||
                    (addValue < 0 && extrusionLength <= cover.partDetails.maxStockLength))) {
                const coverAngle = utils_1.Utils.roundTo3(cover.directionNormal.angleTo(this.directionNormal));
                if (!utils_1.Utils.areEqual(0, coverAngle, consts_1.consts.FLOAT_EPSILON)) {
                    const moveVector = cover.directionNormal.clone().multiplyScalar(addValue).negate();
                    cover.translate(moveVector);
                }
                cover.setScale(this.getScale());
            }
        });
    }
}
exports.default = ExtrusionObjectsBase;
